"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useLocale } from "next-intl";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import {
  type SupportedCurrency,
  type ExchangeRates,
  getCurrencyFromLocale,
  convertFromUSD,
  convertCurrency,
  convertForCharge,
  roundForCharge,
  formatPriceWithCode,
  formatPriceSimple,
  getDisplayPrices,
  getDisplayPricesFromSource,
  isSupportedCurrency,
  CURRENCY_INFO,
} from "@/lib/utils/currency";

/**
 * 사용자가 **직접 고른** 통화만 저장한다. 값이 없으면 locale 기본 통화를 따른다.
 * (옛 키 "locarora-currency"는 locale 기본값을 매번 덮어쓰기만 하고 읽지 않았다 —
 * 그 값을 선택으로 오인하지 않도록 새 키를 쓰고 옛 키는 지운다.)
 */
const PREFERENCE_KEY = "locarora-currency-preference";
const LEGACY_STORAGE_KEY = "locarora-currency";

/** 저장된 사용자 선택 통화. 서버이거나 저장소 접근이 막히면 null. */
export function readCurrencyPreference(): SupportedCurrency | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(PREFERENCE_KEY);
    return isSupportedCurrency(value) ? value : null;
  } catch {
    return null;
  }
}

// 저장소 접근이 막힌 환경(사파리 비공개 모드 등)에서도 이번 세션 동안은 선택이 유지되게
let memoryPreference: SupportedCurrency | null = null;
const preferenceListeners = new Set<() => void>();

function readPreferenceSnapshot(): SupportedCurrency | null {
  return readCurrencyPreference() ?? memoryPreference;
}

function writeCurrencyPreference(currency: SupportedCurrency | null) {
  memoryPreference = currency;
  try {
    if (currency) window.localStorage.setItem(PREFERENCE_KEY, currency);
    else window.localStorage.removeItem(PREFERENCE_KEY);
  } catch {
    // memoryPreference 로 대체
  }
  preferenceListeners.forEach((notify) => notify());
}

function subscribePreference(notify: () => void) {
  preferenceListeners.add(notify);
  // 다른 탭에서 통화를 바꾸면 따라간다
  const onStorage = (event: StorageEvent) => {
    if (event.key === PREFERENCE_KEY) notify();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    preferenceListeners.delete(notify);
    window.removeEventListener("storage", onStorage);
  };
}

const getServerPreference = () => null;

/**
 * 클라이언트 이벤트·effect 안에서 "지금 고객 통화"를 바로 읽는다. CurrencyProvider 의
 * effect 는 자식 effect 보다 늦게 돌기 때문에, 첫 마운트 때 서버 가격 검증을 부르는
 * 코드는 context 값 대신 이 함수를 써야 사용자 선택이 반영된다.
 */
export function resolveClientCurrency(locale: string): SupportedCurrency {
  return readPreferenceSnapshot() ?? getCurrencyFromLocale(locale);
}

interface CurrencyContextValue {
  // 현재 표시·결제 통화 = 사용자 선택(있으면) ?? locale 기본 통화
  selectedCurrency: SupportedCurrency;
  // locale 기본 통화 (서버 렌더·SEO 가 쓰는 값)
  defaultCurrency: SupportedCurrency;
  // 사용자가 locale 기본값과 별개로 통화를 골랐는지
  isCurrencyCustomized: boolean;
  /** 통화 선택. null 이면 선택을 지우고 locale 기본 통화를 따른다. */
  setSelectedCurrency: (currency: SupportedCurrency | null) => void;

  // 환율 데이터
  rates: Partial<ExchangeRates>;
  isLoading: boolean;
  lastUpdated: string | null;

  // SSR 하이드레이션 완료 여부.
  // rates/selectedCurrency를 직접 읽어 자체 변환을 수행하는 컴포넌트는
  // 이 플래그가 true가 되기 전까지 변환을 보류해야 hydration mismatch를 피할 수 있다.
  isHydrated: boolean;

  // 유틸리티 함수 (레거시 - USD 기준)
  /** @deprecated convertFromSource 사용 권장 */
  convertPrice: (amountUSD: number) => number;
  /** @deprecated formatFromSource 사용 권장 */
  formatPrice: (amountUSD: number) => string;
  /** @deprecated getDisplayPricesFromSource 사용 권장 */
  getDisplayPrices: (amountUSD: number) => {
    converted: string;
    original: string;
    showOriginal: boolean;
  };

  // 유틸리티 함수 (상품별 통화 지원)
  /** 소스 통화에서 사용자 선택 통화로 변환 */
  convertFromSource: (amount: number, sourceCurrency: SupportedCurrency) => number;
  /** 소스 통화에서 변환 후 포맷 */
  formatFromSource: (amount: number, sourceCurrency: SupportedCurrency) => string;
  /** formatFromSource 와 동일하되 통화 코드 표기("KRW 3,140") — 카드가 쓴다 */
  formatWithCodeFromSource: (amount: number, sourceCurrency: SupportedCurrency) => string;
  /** 변환된 가격과 원본 가격 정보 */
  getDisplayPricesFromSource: (
    amount: number,
    sourceCurrency: SupportedCurrency
  ) => {
    converted: string;
    original: string;
    showOriginal: boolean;
  };

  // 현재 통화 정보
  currencySymbol: string;
  currencyName: string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

interface CurrencyProviderProps {
  children: ReactNode;
  initialRates?: Record<string, unknown>;
}

export function CurrencyProvider({ children, initialRates }: CurrencyProviderProps) {
  const locale = useLocale();
  const { rates, isLoading, lastUpdated } = useExchangeRates(
    initialRates as Partial<ExchangeRates> | undefined,
  );

  // 첫 렌더는 서버와 같은 locale 기본 통화 — 사용자 선택은 hydration 뒤 effect 에서
  // 반영한다([locale] 은 ISR 이라 서버가 쿠키를 읽을 수 없다). locale 이 바뀌면
  // 기본 통화만 바뀌고 사용자 선택은 유지된다.
  const defaultCurrency = getCurrencyFromLocale(locale);
  // hydration 중에는 getServerPreference(null) → 서버와 같은 렌더, 그 뒤 저장값으로 갱신
  const preferredCurrency = useSyncExternalStore(
    subscribePreference,
    readPreferenceSnapshot,
    getServerPreference,
  );
  const selectedCurrency = preferredCurrency ?? defaultCurrency;

  // SSR에서 initialRates가 내려오면 hydration 전부터 환율 변환 가능.
  // 하위 호환을 위해 isHydrated 필드는 유지하되, initialRates가 있으면 항상 true.
  const hasSSRRates = !!(initialRates && Object.keys(initialRates).length > 0);
  const [isHydrated, setIsHydrated] = useState(hasSSRRates);

  // hydration 완료 마킹 + 옛 저장 키 정리
  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore
    }
    setIsHydrated(true);
  }, []);

  const setSelectedCurrency = useCallback((currency: SupportedCurrency | null) => {
    writeCurrencyPreference(currency && isSupportedCurrency(currency) ? currency : null);
  }, []);

  // 가격 변환 (USD → 선택 통화)
  const convertPrice = useCallback(
    (amountUSD: number): number => {
      if (selectedCurrency === "USD") return amountUSD;

      return convertFromUSD(amountUSD, selectedCurrency, rates);
    },
    [selectedCurrency, rates]
  );

  // 가격 포맷팅
  const formatPrice = useCallback(
    (amountUSD: number): string => {
      const converted = convertPrice(amountUSD);
      return formatPriceSimple(converted, selectedCurrency);
    },
    [convertPrice, selectedCurrency]
  );

  // 환산 + USD 가격 함께 표시 (레거시)
  const getDisplayPricesWrapper = useCallback(
    (amountUSD: number) => {
      return getDisplayPrices(amountUSD, selectedCurrency, rates);
    },
    [selectedCurrency, rates]
  );

  // 소스 통화에서 사용자 선택 통화로 변환 — 결제 청구가와 동일한 공식
  // (`convertForCharge` + `roundForCharge` ceil)을 사용해 고객이 checkout에서
  // 보는 금액과 실제 결제 금액, 웹훅 authoritative 재계산 값이 모두 한
  // 숫자로 일치하도록. 이전에는 `convertCurrency` (display_rate + Math.round)
  // 를 썼는데, 결제 경로는 convertForCharge + ceil을 쓰는 바람에 같은
  // checkout 화면의 아이템 라인과 소계가 1원씩 어긋났다(예: 23,600원 2일
  // 대여를 아이템은 2,601엔, 소계는 2,600엔으로 표시).
  const convertFromSource = useCallback(
    (amount: number, sourceCurrency: SupportedCurrency): number => {
      if (sourceCurrency === selectedCurrency) return amount;
      return convertForCharge(amount, sourceCurrency, selectedCurrency, rates);
    },
    [selectedCurrency, rates]
  );

  // 소스 통화에서 변환 후 포맷 — roundForCharge로 ceil 반올림
  const formatFromSource = useCallback(
    (amount: number, sourceCurrency: SupportedCurrency): string => {
      if (sourceCurrency === selectedCurrency) {
        return formatPriceSimple(amount, selectedCurrency);
      }
      const charged = convertForCharge(amount, sourceCurrency, selectedCurrency, rates);
      const rounded = roundForCharge(charged, selectedCurrency);
      return formatPriceSimple(rounded, selectedCurrency);
    },
    [selectedCurrency, rates]
  );

  // 코드 표기판. formatFromSource 와 반올림 정책이 반드시 같아야 한다 —
  // 예전엔 카드(코드 표기)만 Math.round 를 써서 상세/결제(ceil)보다 1단위 싸게
  // 표시됐다(3,000 KRW → 카드 JPY 331 vs 상세 ¥332).
  const formatWithCodeFromSource = useCallback(
    (amount: number, sourceCurrency: SupportedCurrency): string => {
      if (sourceCurrency === selectedCurrency) {
        return formatPriceWithCode(amount, selectedCurrency);
      }
      const charged = convertForCharge(amount, sourceCurrency, selectedCurrency, rates);
      const rounded = roundForCharge(charged, selectedCurrency);
      return formatPriceWithCode(rounded, selectedCurrency);
    },
    [selectedCurrency, rates]
  );

  // 변환된 가격과 원본 가격 정보 (상품별 통화 지원)
  const getDisplayPricesFromSourceWrapper = useCallback(
    (amount: number, sourceCurrency: SupportedCurrency) => {
      return getDisplayPricesFromSource(amount, sourceCurrency, selectedCurrency, rates);
    },
    [selectedCurrency, rates]
  );

  // 현재 통화 정보
  const currencyInfo = CURRENCY_INFO[selectedCurrency];

  const value: CurrencyContextValue = {
    selectedCurrency,
    defaultCurrency,
    isCurrencyCustomized: preferredCurrency !== null,
    setSelectedCurrency,
    rates,
    isLoading,
    lastUpdated: lastUpdated || null,
    isHydrated,
    // 레거시 메서드 (USD 기준)
    convertPrice,
    formatPrice,
    getDisplayPrices: getDisplayPricesWrapper,
    // 상품별 통화 지원 메서드
    convertFromSource,
    formatFromSource,
    formatWithCodeFromSource,
    getDisplayPricesFromSource: getDisplayPricesFromSourceWrapper,
    // 통화 정보
    currencySymbol: currencyInfo.symbol,
    currencyName: currencyInfo.name,
  };

  // SSR 하이드레이션 전에는 소스 통화 그대로 표시 (hydration mismatch 방지)
  // isHydrated만으로 판단 — 환율 데이터 유무와 무관하게 서버/클라이언트 일치 보장
  if (!isHydrated) {
    const ssrCurrency = defaultCurrency;
    const ssrCurrencyInfo = CURRENCY_INFO[ssrCurrency];
    return (
      <CurrencyContext.Provider
        value={{
          ...value,
          selectedCurrency: ssrCurrency,
          isCurrencyCustomized: false,
          isHydrated: false,
          convertPrice: (amount) => amount,
          formatPrice: (amount) => formatPriceSimple(amount, ssrCurrency),
          getDisplayPrices: (amount) => ({
            converted: formatPriceSimple(amount, ssrCurrency),
            original: formatPriceSimple(amount, ssrCurrency),
            showOriginal: false,
          }),
          // 상품별 통화 지원 - SSR 시에는 소스 통화 그대로 표시
          convertFromSource: (amount) => amount,
          formatFromSource: (amount, sourceCurrency) => formatPriceSimple(amount, sourceCurrency),
          formatWithCodeFromSource: (amount, sourceCurrency) => formatPriceWithCode(amount, sourceCurrency),
          getDisplayPricesFromSource: (amount, sourceCurrency) => ({
            converted: formatPriceSimple(amount, sourceCurrency),
            original: formatPriceSimple(amount, sourceCurrency),
            showOriginal: false,
          }),
          currencySymbol: ssrCurrencyInfo?.symbol || "$",
          currencyName: ssrCurrencyInfo?.name || "US Dollar",
        }}
      >
        {children}
      </CurrencyContext.Provider>
    );
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * 통화 Context 사용 훅
 *
 * @example
 * const { selectedCurrency, formatFromSource, setSelectedCurrency } = useCurrency();
 *
 * // 가격 표시
 * <span>{formatFromSource(50000, "KRW")}</span> // $37.04 또는 ₩50,000
 *
 * // 통화 변경 (언어와 별개) / 선택 해제 → locale 기본 통화
 * setSelectedCurrency("JPY");
 * setSelectedCurrency(null);
 */
export function useCurrency() {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }

  return context;
}

export default CurrencyContext;
